import React from "react";
import { Camera, ChevronDown, Check } from "lucide-react";
import { formatDateString, formatPhoneNumber } from "../../utils";
import {
  getMobileDeptDropdownTriggerClass,
  getMobileDeptOptionButtonClass,
  getMobileFaithStateButtonClass,
  MOBILE_DANGER_OUTLINE_BUTTON_CLASS,
  MOBILE_DEPT_DROPDOWN_PANEL_CLASS,
  MOBILE_FAITH_STATE_OPTIONS,
  MOBILE_FORM_INPUT_BASE_CLASS,
  MOBILE_GENDER_TOGGLE_WRAPPER_CLASS,
  MOBILE_FORM_LABEL_CLASS,
  MOBILE_FORM_TEXTAREA_CLASS,
  getMobileGenderButtonClass,
} from "./mobileListUtils";

export default function MobileDetailEditForm({
  isItemTeacher,
  teacherFormData,
  setTeacherFormData,
  formData,
  setFormData,
  departmentOptions,
  isDetailDeptOpen,
  setIsDetailDeptOpen,
  handleImageUpload,
  handleDelete,
  icons,
}) {
  return (
    <div className="p-5 bg-[#F9F9F8] min-h-full pb-24">
      <div className="relative group mb-6 flex justify-center">
        <div className="w-28 h-28 rounded-[2.5rem] bg-white border-4 border-white shadow-lg overflow-hidden flex items-center justify-center relative">
          {(isItemTeacher ? teacherFormData.photoUrl : formData.photoUrl) ? (
            <img
              src={isItemTeacher ? teacherFormData.photoUrl : formData.photoUrl}
              alt=""
              className="w-full h-full object-cover"
            />
          ) : (
            <img
              src={
                isItemTeacher
                  ? (teacherFormData.gender || formData.gender) === "여"
                    ? icons.teacher_female
                    : icons.teacher_male
                  : formData.gender === "남"
                    ? "/images/boy_animated_light.png"
                    : "/images/girl_animated_light.png"
              }
              alt=""
              className="w-full h-full object-contain p-2"
            />
          )}
          <label className="absolute inset-0 bg-black/0 hover:bg-black/10 transition cursor-pointer flex items-center justify-center group-hover:bg-black/20">
            <input
              id={`mobile-detail-photo-${isItemTeacher ? "teacher" : "student"}`}
              name={`mobile_detail_photo_${isItemTeacher ? "teacher" : "student"}`}
              type="file"
              accept="image/*"
              onChange={handleImageUpload}
              className="hidden"
            />
            <div className="w-8 h-8 rounded-full bg-white/90 shadow-sm flex items-center justify-center text-[#354F38] opacity-0 group-hover:opacity-100 transition-all scale-75 group-hover:scale-100">
              <Camera size={16} />
            </div>
          </label>
        </div>
        <div className="absolute -bottom-2 bg-[#354F38] text-white p-1.5 rounded-full shadow-md border-2 border-white pointer-events-none">
          <Camera size={14} />
        </div>
      </div>

      <form className="space-y-4">
        <div>
          <label className={MOBILE_FORM_LABEL_CLASS}>
            이름 <span className="text-red-500">*</span>
          </label>
          <div className="flex gap-2">
            <input
              id={`mobile-detail-name-${isItemTeacher ? "teacher" : "student"}`}
              name={`mobile_detail_name_${isItemTeacher ? "teacher" : "student"}`}
              required
              className={["flex-1", MOBILE_FORM_INPUT_BASE_CLASS, "min-w-0"].join(" ")}
              value={isItemTeacher ? teacherFormData.name : formData.name}
              onChange={(e) =>
                isItemTeacher
                  ? setTeacherFormData({ ...teacherFormData, name: e.target.value })
                  : setFormData({ ...formData, name: e.target.value })
              }
              placeholder="이름 입력"
            />
            <div className={MOBILE_GENDER_TOGGLE_WRAPPER_CLASS}>
              <button
                type="button"
                onClick={() =>
                  isItemTeacher
                    ? setTeacherFormData({ ...teacherFormData, gender: "남" })
                    : setFormData({ ...formData, gender: "남" })
                }
                className={getMobileGenderButtonClass(
                  isItemTeacher ? teacherFormData.gender : formData.gender,
                  "남"
                )}
              >
                남
              </button>
              <button
                type="button"
                onClick={() =>
                  isItemTeacher
                    ? setTeacherFormData({ ...teacherFormData, gender: "여" })
                    : setFormData({ ...formData, gender: "여" })
                }
                className={getMobileGenderButtonClass(
                  isItemTeacher ? teacherFormData.gender : formData.gender,
                  "여"
                )}
              >
                여
              </button>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className={MOBILE_FORM_LABEL_CLASS}>생년월일</label>
            <input
              id={`mobile-detail-birth-${isItemTeacher ? "teacher" : "student"}`}
              name={`mobile_detail_birth_${isItemTeacher ? "teacher" : "student"}`}
              className={["w-full", MOBILE_FORM_INPUT_BASE_CLASS].join(" ")}
              placeholder="YYYY-MM-DD"
              value={isItemTeacher ? teacherFormData.birth : formData.birth}
              onChange={(e) =>
                isItemTeacher
                  ? setTeacherFormData({
                      ...teacherFormData,
                      birth: formatDateString(e.target.value),
                    })
                  : setFormData({ ...formData, birth: formatDateString(e.target.value) })
              }
            />
          </div>
          <div>
            <label className={MOBILE_FORM_LABEL_CLASS}>연락처</label>
            <input
              id={`mobile-detail-phone-${isItemTeacher ? "teacher" : "student"}`}
              name={`mobile_detail_phone_${isItemTeacher ? "teacher" : "student"}`}
              className={["w-full", MOBILE_FORM_INPUT_BASE_CLASS].join(" ")}
              placeholder="010-0000-0000"
              type="tel"
              value={isItemTeacher ? teacherFormData.phone : formData.phone}
              onChange={(e) =>
                isItemTeacher
                  ? setTeacherFormData({
                      ...teacherFormData,
                      phone: formatPhoneNumber(e.target.value),
                    })
                  : setFormData({ ...formData, phone: formatPhoneNumber(e.target.value) })
              }
            />
          </div>
        </div>

        {!isItemTeacher && (
          <div>
            <label className={MOBILE_FORM_LABEL_CLASS}>학교/직장</label>
            <input
              id="mobile-detail-school-student"
              name="mobile_detail_school_student"
              className={["w-full", MOBILE_FORM_INPUT_BASE_CLASS].join(" ")}
              value={formData.school}
              onChange={(e) => setFormData({ ...formData, school: e.target.value })}
              placeholder="학교 또는 직장명"
            />
          </div>
        )}
        {isItemTeacher && (
          <div>
            <label className={MOBILE_FORM_LABEL_CLASS}>학교/직장</label>
            <input
              id="mobile-detail-school-teacher"
              name="mobile_detail_school_teacher"
              className={["w-full", MOBILE_FORM_INPUT_BASE_CLASS].join(" ")}
              value={teacherFormData.school || ""}
              onChange={(e) =>
                setTeacherFormData({ ...teacherFormData, school: e.target.value })
              }
              placeholder="학교 또는 직장명"
            />
          </div>
        )}

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className={MOBILE_FORM_LABEL_CLASS}>부서</label>
            <div className="space-y-2">
              <div
                onClick={() => setIsDetailDeptOpen(!isDetailDeptOpen)}
                className={getMobileDeptDropdownTriggerClass(isDetailDeptOpen)}
              >
                <span
                  className={
                    !(isItemTeacher ? teacherFormData.grade : formData.grade)
                      ? "text-gray-400"
                      : "text-gray-900"
                  }
                >
                  {(isItemTeacher ? teacherFormData.grade : formData.grade) || "부서 선택"}
                </span>
                <ChevronDown
                  size={16}
                  className={`text-gray-400 transition-transform ${isDetailDeptOpen ? "rotate-180" : ""}`}
                />
              </div>

              {isDetailDeptOpen && (
                <div className={MOBILE_DEPT_DROPDOWN_PANEL_CLASS}>
                  {departmentOptions.map((dept) => (
                    <button
                      key={dept}
                      type="button"
                      onClick={() => {
                        const value = dept;
                        if (isItemTeacher) {
                          setTeacherFormData((prev) => ({ ...prev, grade: value }));
                        } else {
                          setFormData((prev) => ({ ...prev, grade: value }));
                        }
                        setIsDetailDeptOpen(false);
                      }}
                      className={getMobileDeptOptionButtonClass(
                        isItemTeacher ? teacherFormData.grade : formData.grade,
                        dept
                      )}
                    >
                      {dept}
                      {(isItemTeacher ? teacherFormData.grade : formData.grade) === dept && (
                        <Check size={14} />
                      )}
                    </button>
                  ))}
                </div>
              )}
              {((isItemTeacher ? teacherFormData.grade : formData.grade) === "기타(직접 입력)" ||
                ((isItemTeacher ? teacherFormData.grade : formData.grade) &&
                  !departmentOptions.some(
                    (opt) =>
                      opt === String(isItemTeacher ? teacherFormData.grade : formData.grade).trim()
                  ))) && (
                <input
                  id={`mobile-detail-grade-custom-${isItemTeacher ? "teacher" : "student"}`}
                  name={`mobile_detail_grade_custom_${isItemTeacher ? "teacher" : "student"}`}
                  className={["w-full", MOBILE_FORM_INPUT_BASE_CLASS].join(" ")}
                  placeholder="부서명 직접 입력"
                  value={
                    (isItemTeacher ? teacherFormData.grade : formData.grade) ===
                    "기타(직접 입력)"
                      ? ""
                      : isItemTeacher
                        ? teacherFormData.grade
                        : formData.grade
                  }
                  onChange={(e) =>
                    isItemTeacher
                      ? setTeacherFormData((prev) => ({ ...prev, grade: e.target.value }))
                      : setFormData((prev) => ({ ...prev, grade: e.target.value }))
                  }
                />
              )}
            </div>
          </div>
          <div>
            <label className={MOBILE_FORM_LABEL_CLASS}>
              {isItemTeacher ? "담당" : "반 (선택)"}
            </label>
            <input
              id={`mobile-detail-class-or-charge-${isItemTeacher ? "teacher" : "student"}`}
              name={`mobile_detail_class_or_charge_${isItemTeacher ? "teacher" : "student"}`}
              className={["w-full", MOBILE_FORM_INPUT_BASE_CLASS].join(" ")}
              placeholder={isItemTeacher ? "예: 찬양팀" : "예: 1반"}
              value={isItemTeacher ? teacherFormData.charge : formData.class}
              onChange={(e) =>
                isItemTeacher
                  ? setTeacherFormData({ ...teacherFormData, charge: e.target.value })
                  : setFormData({ ...formData, class: e.target.value })
              }
            />
          </div>
        </div>

        {isItemTeacher && (
          <div>
            <label className={MOBILE_FORM_LABEL_CLASS}>직분</label>
            <input
              id="mobile-detail-role-teacher"
              name="mobile_detail_role_teacher"
              className={["w-full", MOBILE_FORM_INPUT_BASE_CLASS].join(" ")}
              placeholder="예: 교사, 부장"
              value={teacherFormData.role}
              onChange={(e) =>
                setTeacherFormData({ ...teacherFormData, role: e.target.value })
              }
            />
          </div>
        )}

        {!isItemTeacher && (
          <>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={MOBILE_FORM_LABEL_CLASS}>
                  보호자1 관계
                </label>
                <input
                  id="mobile-detail-guardian1-name"
                  name="mobile_detail_guardian1_name"
                  className={["w-full", MOBILE_FORM_INPUT_BASE_CLASS].join(" ")}
                  placeholder="어머니"
                  value={formData.guardian1Name || ""}
                  onChange={(e) =>
                    setFormData({ ...formData, guardian1Name: e.target.value })
                  }
                />
              </div>
              <div>
                <label className={MOBILE_FORM_LABEL_CLASS}>
                  보호자1 연락처
                </label>
                <input
                  id="mobile-detail-guardian1-phone"
                  name="mobile_detail_guardian1_phone"
                  className={["w-full", MOBILE_FORM_INPUT_BASE_CLASS].join(" ")}
                  placeholder="010-0000-0000"
                  type="tel"
                  value={formData.guardian1Phone || ""}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      guardian1Phone: formatPhoneNumber(e.target.value),
                    })
                  }
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={MOBILE_FORM_LABEL_CLASS}>
                  보호자2 관계
                </label>
                <input
                  id="mobile-detail-guardian2-name"
                  name="mobile_detail_guardian2_name"
                  className={["w-full", MOBILE_FORM_INPUT_BASE_CLASS].join(" ")}
                  placeholder="아버지"
                  value={formData.guardian2Name || ""}
                  onChange={(e) =>
                    setFormData({ ...formData, guardian2Name: e.target.value })
                  }
                />
              </div>
              <div>
                <label className={MOBILE_FORM_LABEL_CLASS}>
                  보호자2 연락처
                </label>
                <input
                  id="mobile-detail-guardian2-phone"
                  name="mobile_detail_guardian2_phone"
                  className={["w-full", MOBILE_FORM_INPUT_BASE_CLASS].join(" ")}
                  placeholder="010-0000-0000"
                  type="tel"
                  value={formData.guardian2Phone || ""}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      guardian2Phone: formatPhoneNumber(e.target.value),
                    })
                  }
                />
              </div>
            </div>
          </>
        )}

        <div>
          <label className={MOBILE_FORM_LABEL_CLASS}>주소</label>
          <input
            id={`mobile-detail-address-${isItemTeacher ? "teacher" : "student"}`}
            name={`mobile_detail_address_${isItemTeacher ? "teacher" : "student"}`}
            className={["w-full", MOBILE_FORM_INPUT_BASE_CLASS].join(" ")}
            value={isItemTeacher ? teacherFormData.address : formData.address}
            onChange={(e) =>
              isItemTeacher
                ? setTeacherFormData({ ...teacherFormData, address: e.target.value })
                : setFormData({ ...formData, address: e.target.value })
            }
            placeholder="주소를 입력하세요"
          />
        </div>

        {!isItemTeacher && (
          <div>
            <label className={MOBILE_FORM_LABEL_CLASS}>신앙상태</label>
            <div className="grid grid-cols-3 gap-2 w-full mt-2">
              {MOBILE_FAITH_STATE_OPTIONS.map((stateItem) => (
                <button
                  type="button"
                  key={stateItem.label}
                  onClick={() => setFormData({ ...formData, faithState: stateItem.label })}
                  className={getMobileFaithStateButtonClass(
                    formData.faithState === stateItem.label,
                    stateItem
                  )}
                >
                  {stateItem.label}
                </button>
              ))}
            </div>
          </div>
        )}
        {isItemTeacher && (
          <div>
            <label className={MOBILE_FORM_LABEL_CLASS}>신앙상태</label>
            <div className="grid grid-cols-3 gap-2 w-full mt-2">
              {MOBILE_FAITH_STATE_OPTIONS.map((stateItem) => (
                <button
                  type="button"
                  key={stateItem.label}
                  onClick={() =>
                    setTeacherFormData({ ...teacherFormData, faithState: stateItem.label })
                  }
                  className={getMobileFaithStateButtonClass(
                    (teacherFormData.faithState || "기존신자") === stateItem.label,
                    stateItem
                  )}
                >
                  {stateItem.label}
                </button>
              ))}
            </div>
          </div>
        )}

        <div>
          <label className={MOBILE_FORM_LABEL_CLASS}>비고</label>
          <textarea
            id={`mobile-detail-notes-${isItemTeacher ? "teacher" : "student"}`}
            name={`mobile_detail_notes_${isItemTeacher ? "teacher" : "student"}`}
            className={["w-full", MOBILE_FORM_TEXTAREA_CLASS].join(" ")}
            value={isItemTeacher ? teacherFormData.notes : formData.notes}
            onChange={(e) =>
              isItemTeacher
                ? setTeacherFormData({ ...teacherFormData, notes: e.target.value })
                : setFormData({ ...formData, notes: e.target.value })
            }
            placeholder="특이사항을 입력하세요"
          />
        </div>

        <button
          type="button"
          onClick={handleDelete}
          className={MOBILE_DANGER_OUTLINE_BUTTON_CLASS}
        >
          삭제하기
        </button>
      </form>
    </div>
  );
}
